import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FunctionalHelpJsonModel } from '../../../functionalHelp/functionalHelp.json.model';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Store } from '@ngxs/store';
import { FetchOptionsActions } from '../../state/fetchOptions.actions';
import { FunctionHelpService } from '../../../functionalHelp/services/functionHelp.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FetchOptionsState } from '../../state/fetchOptions.state';
import { ConsumerJsonModel } from '../../../consumer/consumer.json.model';
import { SubjectJsonModel } from '../../../subject/subject.json.model';
import { CollectionFilter } from '../../jsonApi/BaseCollectionService';
import { AppSelectors } from '../../../app.state';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateComponent } from '../translation/translation.component';
import { MainPaginationComponent } from '../main-pagination/main-pagination.component';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { getMinFilterLengthText } from '../../util';
import { TypeCBComponent } from '../../../utils/component/type-filter-combobox/type-cb.component';
import { CompetencyFilterCBComponent } from '../../../utils/component/competency-filter-combobox/competency-cb.component';
import { StateFilterCBComponent } from '../../../utils/component/state-filter-combobox/state-cb.component';

/**
 * How to use in other components:
 *
 public showFunctionalHelpDialog(): void {
    const functionalHelpJsonModel = new FunctionalHelpJsonModel({
      id: '',
      name: '',
    });

    const dialogRef = this.dialog.open(FunctionalHelpDialogComponent, {
      minWidth: '1000px',
      height: '70vh',
      data: functionalHelpJsonModel,
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dialogData: FunctionalHelpJsonModel) => {
        if (dialogData) {
          console.log(`ID: ${dialogData.id}, Name: ${dialogData.name}`);
        };
      });
  }
*/

@Component({
  selector: 'pixl-functional-help-dialog',
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogContent,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatCardModule,
    MatIconModule,
    FormsModule,
    CommonModule,
    MatSortModule,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    MainPaginationComponent,
    TranslateComponent,
    FreeFilterInputComponent,
    TypeCBComponent,
    CompetencyFilterCBComponent,
    StateFilterCBComponent,
  ],
  templateUrl: './functional-help-dialog.component.html',
  styleUrl: './functional-help-dialog.component.scss'
})
export class FunctionalHelpDialogComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  readonly dialogRef = inject(MatDialogRef<FunctionalHelpDialogComponent>);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private readonly store = inject(Store);
  private functionHelpService = inject(FunctionHelpService);
  private destroyRef = inject(DestroyRef);
  private appState$ = this.store.select(AppSelectors.appState);
  private cdr = inject(ChangeDetectorRef);
  private nameFilter: string | undefined;
  private exerciseKindFilter: string | undefined;
  private competencyFilter: string | undefined;
  private stateFilter: string | undefined;
  private filterActive = false;

  public readonly freeFilterLen = 3;
  // Die Datenquelle einmal instanziieren
  public dataSource = new MatTableDataSource<FunctionalHelpJsonModel>([] as FunctionalHelpJsonModel[]);
  public displayedColumns: string[] = ['name', 'exerciseKind', 'competency'];

  public filters: CollectionFilter[] = [];
  public consumers: ConsumerJsonModel[] = [];
  public competencies: SubjectJsonModel[] = [];
  public data = inject<FunctionalHelpJsonModel>(MAT_DIALOG_DATA);

  public ngOnInit(): void {
    // Sortierung nur einmal zuweisen
    this.dataSource.sort = this.sort;

    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        this.consumers = consumers;
        this.competencies = subjects;
        this.getListData();
      }
    });
  }

  private sortData(sort: Sort): void {
    this.store.dispatch(new FetchOptionsActions.UpdateSortFieldName(sort.active));
    this.store.dispatch(new FetchOptionsActions.UpdateSortDirection(sort.direction));
    this.getListData();
  }

  private getListData(): void {
    if (this.filterActive) {
      this.store.dispatch(new FetchOptionsActions.ResetPageNumber());
      this.filterActive = false;
    }

    this.functionHelpService.getAllWithPagination({}, {
      pageNumber: this.store.selectSnapshot(FetchOptionsState.getPageNumber),
      pageSize: this.store.selectSnapshot(FetchOptionsState.getPageSize),
      filters: this.filters,
      sortFieldName: this.store.selectSnapshot(FetchOptionsState.getSortFieldName),
      sortDirection: this.store.selectSnapshot(FetchOptionsState.getSortDirection)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const [data] = response;
          const functionalHelps = data.map(r => {
            r.setConsumer(this.consumers.find((model) => model.id == r.consumerId) as ConsumerJsonModel);
            r.setCompetency(this.competencies.find((model) => model.treeId == r.subjectId) as SubjectJsonModel);
            return r;
          });
          this.dataSource.data = functionalHelps;
          this.cdr.markForCheck();
        },
        error: () => {
          // is handled in requestInterceptor.ts
        },
      });
  }

  private setFilters(): void {
    this.filters = [];

    if (this.nameFilter) {
      this.filters.push({ fieldName: 'name', value: this.nameFilter });
      this.filterActive = true;
    }

    if (this.exerciseKindFilter) {
      this.filters.push({ fieldName: 'exerciseKind', value: this.exerciseKindFilter });
      this.filterActive = true;
    }

    if (this.competencyFilter) {
      this.filters.push({ fieldName: 'subjectId', value: this.competencyFilter });
      this.filterActive = true;
    }

    if (this.stateFilter) {
      this.filters.push({ fieldName: 'approval', value: this.stateFilter });
      this.filterActive = true;
    }

    this.getListData();
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public paginationChanged = (): void => {
    this.getListData();
  };

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public setNameFilter(ev: string): void {
    this.nameFilter = (ev.length === 0 ? undefined : ev);
    this.setFilters();
  }

  public setKindFilter(ev: string): void {
    this.exerciseKindFilter = ev;
    this.setFilters();
  }

  public setCompetencyFilter(ev: string): void {
    this.competencyFilter = ev;
    this.setFilters();
  }

  public setStateFilter(ev: string): void {
    this.stateFilter = ev;
    this.setFilters();
  }

  // Den ausgewählten Datensatz in der separaten Variable speichern
  public setSelectedRow(selectedRow: FunctionalHelpJsonModel): void {
    this.data = selectedRow;
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  // Den ausgewählten Datensatz aus der separaten Variable zurückgeben
  public onSave(): void {
    this.dialogRef.close(this.data);
  }
}
